/**
 * Inlined literals for the `ProductListTableRowContentStyle` and `DetailType` members used when
 * building product-list table rows.
 *
 * Why this file exists: importing those enums as *values* pulls the entire
 * `@vanguard/trade-ui-components-lib-ng-18` bundle — a single 1.44 MB module — into the importer's
 * runtime graph. Only two selectors need these five members, but selectors are reached by most
 * component suites, so every one of them was parsing and compiling that bundle for five strings.
 * A narrower import is impossible: the library's `exports` map only exposes `"."`, and its subpaths
 * ship `.d.ts` files with no runtime module beside them.
 *
 * `import type` is erased at emit, so this module requires nothing at runtime, and the components
 * that genuinely render trade-ui widgets still import the real library.
 *
 * The literals cannot silently drift. Each raw literal is declared *without* a cast and then
 * asserted against the real enum member, so changing a literal here — or the library changing its
 * value — fails `tsc`. Asserting before the cast matters: casting first would satisfy the assertion
 * trivially and the guard would be worthless.
 */
import type {
  DetailType,
  ProductListTableRowContentStyle,
} from '@vanguard/trade-ui-components-lib-ng-18';

// Raw, uncast literals — these are what the assertions below actually check.
const BOLD = 'BOLD';
const SMALL = 'SMALL';
const XSMALL = 'XSMALL';
const BOLD_TICKER = 'BOLD_TICKER';
const TEXT = 'TEXT';

type Assert<T extends true> = T;

/**
 * Compile-time proof that each inlined literal still equals the library enum member it stands in
 * for. Mutate any literal above and this fails to compile.
 */
export type TradeUiEnumLiteralsInSync = [
  Assert<`${ProductListTableRowContentStyle.BOLD}` extends typeof BOLD ? true : false>,
  Assert<`${ProductListTableRowContentStyle.SMALL}` extends typeof SMALL ? true : false>,
  Assert<`${ProductListTableRowContentStyle.XSMALL}` extends typeof XSMALL ? true : false>,
  Assert<
    `${ProductListTableRowContentStyle.BOLD_TICKER}` extends typeof BOLD_TICKER ? true : false
  >,
  Assert<`${DetailType.TEXT}` extends typeof TEXT ? true : false>,
];

// Cast to the *specific member* rather than the enum, so discriminated unions such as `DetailTypes`
// still narrow correctly. Safe because of the assertions above.
export const CONTENT_STYLE_BOLD = BOLD as ProductListTableRowContentStyle.BOLD;
export const CONTENT_STYLE_SMALL = SMALL as ProductListTableRowContentStyle.SMALL;
export const CONTENT_STYLE_XSMALL = XSMALL as ProductListTableRowContentStyle.XSMALL;
export const CONTENT_STYLE_BOLD_TICKER = BOLD_TICKER as ProductListTableRowContentStyle.BOLD_TICKER;
export const DETAIL_TYPE_TEXT = TEXT as DetailType.TEXT;
